import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { mString } from '/lib/helpers/utility';
import Honeybadger from '@honeybadger-io/js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';

import { requestApi } from 'api/Api';
import { deleteCptCode } from 'api/CptCodes';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { useCPTCodes } from 'lib/hooks/queries/cptCodes/useCPTCodes';
import usePageTitle from 'lib/hooks/usePageTitle';

import AGTable from 'components/shared/AGTable/AGTable';
import DisplayButton from 'components/shared/AGTable/DisplayButton';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Header from 'components/shared/Header/Header';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Pagination from 'components/shared/Pagination/Pagination';
import Allowed from 'components/shared/Permissions/Allowed';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Switch from 'components/shared/Switch/Switch';

import NewAndUpdateCPT from './NewAndUpdate';

export default function CPTCodes() {
  const [deleteCPTCodesModal, setDeleteCPTCodesModal] = useState(null);
  const [selectedCPTCode, setSelectedCPTCode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const Actions = ({ data }) => {
    const actionOptions = [
      {
        label: 'Update',
        icon: 'edit-2-mono',
        onClick: () => {
          setSelectedCPTCode(data);
          setShowModal(true);
          setMode('update');
        },
        permission: { requiredPermissions: ['cpt-codes.update'] }
      },
      {
        label: 'Delete',
        icon: 'trash-mono',
        onClick: () => {
          setSelectedCPTCode(data);
          setDeleteCPTCodesModal(true);
        },
        permission: { requiredPermissions: ['cpt-codes.delete'] }
      }
    ];

    return (
      <Popover
        isFixed
        position="left"
        isBorder={false}
        options={actionOptions}
        className="flex h-full items-center"
      />
    );
  };
  const handleEMCode = async (id, em_code) => {
    try {
      const resData = await requestApi({
        url: `/api/codes/cpt/update`,
        params: { fields: { code: id, em_code } },
        navigate
      });
      const { code } = resData || {};
      switch (code) {
        case 0:
          showAlert({ title: 'CPT Code updated successfully', color: 'primary' });
          queryClient.invalidateQueries(['cptCodes']);
          break;
        default:
          Honeybadger.notify(`updateEMCode, There's been an unexpected error.`);
          break;
      }
    } catch (error) {
      Honeybadger.notify(`updateEMCode: ${error}`);
    }
  };
  const switchEMCode = ({ data, value }) => {
    return (
      <div className="flex h-full items-center justify-center">
        <Switch checked={!!value} onChange={(e) => handleEMCode(data?.code, e)} />
      </div>
    );
  };
  const cols = [
    { field: 'code', headerName: 'Code', maxWidth: 150 },
    { field: 'name', headerName: 'Name', width: 300 },
    { field: 'concept_id', headerName: 'Concept ID' },
    {
      field: 'limiting_charge',
      headerName: 'Limiting Charge',
      valueFormatter: ({ value }) => mString(value)
    },
    {
      field: 'par_amount',
      headerName: 'Par Amount',
      valueFormatter: ({ value }) => mString(value)
    },
    {
      field: 'non_par_amount',
      headerName: 'Non Par Amount',
      valueFormatter: ({ value }) => mString(value)
    },
    { field: 'em_code', headerName: 'EM Code', cellRenderer: switchEMCode },
    {
      field: 'base_unit',
      headerName: 'Base Unit',
      cellRenderer: ({ value }) => (
        <div className="flex h-full items-center justify-center">{value !== null ? value : ''}</div>
      )
    },
    {
      field: 'actions',
      headerName: '',
      maxWidth: 100,
      minWidth: 100,
      resizable: false,
      cellRenderer: Actions
    }
  ];
  return (
    <Allowed requiredPermissions="cpt-codes.read" showMessage showIllustration>
      <TableContextProvider name="cpt_codes" pagination cols={cols}>
        <Table
          setShowModal={setShowModal}
          setMode={setMode}
          selectedCPTCode={selectedCPTCode}
          deleteCPTCodesModal={deleteCPTCodesModal}
          setDeleteCPTCodesModal={setDeleteCPTCodesModal}
        />
      </TableContextProvider>
      {!!showModal && (
        <NewAndUpdateCPT
          mode={mode}
          selectedCPTCode={selectedCPTCode}
          hideModal={() => setShowModal(false)}
          showModal={!!showModal}
        />
      )}
    </Allowed>
  );
}

function Table({
  selectedCPTCode,
  setMode,
  setShowModal,
  deleteCPTCodesModal,
  setDeleteCPTCodesModal
}) {
  usePageTitle('CPT Codes');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const queryClient = useQueryClient();
  const { limit, page, setPage, sort, setTableLoading } = useTableContext();
  const navigate = useNavigate();
  const {
    data = {},
    isLoading,
    isFetching
  } = useCPTCodes({
    params: { searchTerm: debouncedSearchTerm, page, sort, limit, withCount: true },
    dependencies: [debouncedSearchTerm, page, sort, limit],
    page,
    sort
  });
  const cptCodes = data?.cpt || [];
  const mutateDelete = useMutation({
    mutationFn: () => deleteCptCode(navigate, { code: selectedCPTCode.code }),
    onSuccess: (data) => {
      switch (data?.code) {
        case 0:
          queryClient.invalidateQueries(['cptCodes']);
          setDeleteCPTCodesModal(null);
          showAlert({ title: 'CPT Code deleted successfully!', color: 'success' });
          break;
        default:
          showAlert({ title: 'Failed to delete CPT Code.', color: 'danger' });
          break;
      }
    }
  });
  useEffect(() => {
    setTableLoading(isLoading || isFetching);
  }, [isLoading, isFetching, setTableLoading]);
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    handler();
    return () => {
      handler.cancel();
    };
  }, [searchTerm]);
  const defaultColDef = useMemo(() => {
    return { flex: 1, minWidth: 100 };
  }, []);
  return (
    <div className="flex h-full flex-col">
      <Header title="CPT Codes">
        <div className="!mt-2 flex items-center gap-2">
          <DisplayButton />
          <Input
            data-qa="search-cpt-codes"
            type="string"
            placeholder="Search cpt codes"
            value={searchTerm}
            icon="search-linear"
            inputWrapperClassName="h-[30px]"
            className="md:w-[200px]"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            rightText={
              searchTerm && (
                <Icon
                  icon="close-linear"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                />
              )
            }
          />
          <Allowed requiredPermissions="cpt-codes.create">
            <Button
              data-qa="new-cpt-code-btn"
              size="small"
              type="primary"
              text="New CPT Code"
              onClick={() => {
                setShowModal(true);
                setMode('create');
              }}
            />
          </Allowed>
        </div>
      </Header>
      <div className="flex h-full flex-col">
        <div className="ag-theme-quartz !mb-0 h-full">
          <AGTable data={cptCodes} defaultColDef={defaultColDef} />
        </div>
        <div className="flex items-center justify-between rounded-b-lg bg-white !px-5 !py-2">
          {data?.cptCount > 0 && (
            <p className="font-light text-primary-900">
              Showing {(page - 1) * limit} -{' '}
              {data?.cptCount > page * limit ? page * limit : data?.cptCount} out of{' '}
              {data?.cptCount}
            </p>
          )}
          <Pagination
            containerClassName="flex"
            onPageChange={({ selected }) => setPage(selected + 1)}
            perPage={limit}
            totalItems={data?.cptCount}
            page={page}
          />
          {!!deleteCPTCodesModal && (
            <Confirm
              variant="danger"
              primaryBtnTxt="Delete"
              title="Delete CPT Code"
              icon="trash-mono"
              message="Are you sure you want to delete this CPT Code?"
              handleContinue={() => {
                mutateDelete.mutate();
                setDeleteCPTCodesModal(false);
              }}
              handleOpen={!!deleteCPTCodesModal}
              handleClose={() => setDeleteCPTCodesModal(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
